import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { getAppointments, getAppointmentStatusSummary, AppointmentStatusScope } from "../../../api/Appointment";

/**
 * Thanh tab trạng thái kèm số lượng (giống trang Lịch hẹn) cho Lịch tư vấn / Lịch thực hiện.
 *
 * - Danh sách tab lấy từ API thống kê riêng của màn hình (status-consultation / status-action).
 * - Số đếm lấy từ tổng của chính API danh sách (take=1) với cùng bộ lọc đang chọn:
 *   API thống kê bỏ qua tìm kiếm / nhân viên / ngày tạo và có trạng thái đếm lệch với danh sách,
 *   nên dùng nó thì số trên tab không khớp bảng.
 * - Tab và dropdown "Trạng thái" cùng dùng `value` (filters.statuses): chọn 1 tab = lọc đúng 1 trạng thái,
 *   "Tất cả" = bỏ lọc; chọn nhiều trạng thái trong dropdown thì không tab nào sáng.
 */

const STATUS_STYLE: Record<number, { bg: string; border: string }> = {
    1: { bg: "bg-blue-500", border: "border-blue-500" },
    2: { bg: "bg-cyan-500", border: "border-cyan-500" },
    3: { bg: "bg-orange-500", border: "border-orange-500" },
    4: { bg: "bg-green-500", border: "border-green-500" },
    0: { bg: "bg-red-500", border: "border-red-500" },
};
const ALL_STYLE = { bg: "bg-[#3e91ab]", border: "border-[#3e91ab]" };

interface Props {
    scope: Extract<AppointmentStatusScope, "consultation" | "action">;
    /** 1 = lịch tư vấn, 2 = lịch thực hiện */
    typeAppointment: number;
    /** Bộ lọc khác đang áp dụng cho danh sách (không gồm trạng thái) */
    baseParams: {
        search?: string;
        staff_filter?: number[];
        date_from?: string;
        date_to?: string;
    };
    value: number[];
    onChange: (statuses: number[]) => void;
    /** Tăng lên để đếm lại (vd: sau khi kéo thẻ đổi trạng thái ở chế độ Tiến trình) */
    refreshKey?: number;
}

const StatusTabs = ({ scope, typeAppointment, baseParams, value, onChange, refreshKey = 0 }: Props) => {
    const { t } = useTranslation();
    const [tabs, setTabs] = useState<{ status: number; text: string }[]>([]);
    const [counts, setCounts] = useState<Record<string, number | undefined>>({});

    // Danh sách trạng thái của màn hình
    useEffect(() => {
        let cancelled = false;
        getAppointmentStatusSummary(undefined, scope).then((res) => {
            if (cancelled || !res.status) return;
            setTabs(
                res.data
                    .filter((d) => d.status !== "all")
                    .map((d) => ({ status: Number(d.status), text: d.text || String(d.status) }))
            );
        });
        return () => { cancelled = true; };
    }, [scope]);

    // Đếm theo đúng bộ lọc hiện tại
    const paramsKey = JSON.stringify(baseParams);
    const statusesKey = tabs.map((s) => s.status).join(",");
    useEffect(() => {
        if (!tabs.length) return;
        let cancelled = false;
        const count = (statuses?: number[]) =>
            getAppointments({ ...baseParams, type_appointment: typeAppointment, statuses, page: 1, take: 1 })
                .then((res) => (res.status ? res.data?.total ?? 0 : undefined))
                .catch(() => undefined);

        Promise.all([count(), ...tabs.map((s) => count([s.status]))]).then(([all, ...byStatus]) => {
            if (cancelled) return;
            const next: Record<string, number | undefined> = { all };
            tabs.forEach((s, i) => { next[s.status] = byStatus[i]; });
            setCounts(next);
        });
        return () => { cancelled = true; };
        // baseParams so sánh qua paramsKey để không đếm lại mỗi lần render
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [paramsKey, statusesKey, typeAppointment, refreshKey]);

    const activeKey = value.length === 0 ? "all" : value.length === 1 ? String(value[0]) : null;

    const items = [
        { key: "all", label: t("order.all"), style: ALL_STYLE, statuses: [] as number[] },
        ...tabs.map((s) => ({
            key: String(s.status),
            label: s.text,
            style: STATUS_STYLE[s.status] || ALL_STYLE,
            statuses: [s.status],
        })),
    ];

    return (
        <div className="flex overflow-x-auto scrollbar-hide gap-2">
            {items.map((tab) => {
                const active = activeKey === tab.key;
                const count = counts[tab.key];
                return (
                    <button
                        key={tab.key}
                        type="button"
                        onClick={() => onChange(tab.statuses)}
                        className={`
                            flex items-center gap-2 px-3 py-1 text-sm rounded border text-000000e0 transition
                            ${active
                                ? `${tab.style.bg} !text-white ${tab.style.border}`
                                : "bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700"}
                        `}
                    >
                        <span className="whitespace-nowrap">{tab.label}</span>
                        <span className={`px-1.5 text-xs rounded text-white min-w-[20px] text-center ${tab.style.bg}`}>
                            {count ?? "…"}
                        </span>
                    </button>
                );
            })}
        </div>
    );
};

export default StatusTabs;
